import { Box, Stack } from '@mui/material';
import Paper from '@mui/material/Paper';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import classNames from 'classnames';
import * as React from 'react';
import { useSelector } from 'react-redux';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';
import createTheme from '/@/components/theme-provider';
import settings from '/@/core/settings';
import { getSessionCountry } from '/@/store/session/selectors';
import styles from './styles.module.css';

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

function TechnologySlider({
    data,
    onClick,
    border = true,
    absolute = true,
    showTechnology = true,
    showArrows = true,
    imageHeight = 255,
    hideIconWarning = false,
}: {
    data: { label: string; logoPath: string; imgPath: string }[];
    onClick: () => void;
    border?: boolean;
    absolute?: boolean;
    showTechnology?: boolean;
    showArrows?: boolean;
    imageHeight?: number;
    hideIconWarning?: boolean;
}) {
    const theme = useTheme();
    const [activeStep, setActiveStep] = React.useState(0);
    const maxSteps = data.length;
    const country = useSelector(getSessionCountry);
    const materialTheme = createTheme({ app: settings.app.theme });

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    return (
        <ThemeProvider theme={materialTheme}>
            <div className={classNames(styles.root, border && styles.borderSlider)}>
                {showTechnology && (
                    <div
                        className={classNames(
                            border ? styles.logoContainer : styles.insideContainer,
                            absolute && styles.absolute
                        )}
                    >
                        {data?.[activeStep]?.logoPath && (
                            <div
                                className={classNames(
                                    border ? styles.logo : styles.insideLogo,
                                    styles[`technology-${data?.[activeStep]?.label}`]
                                )}
                                style={{ backgroundImage: `url('${data?.[activeStep]?.logoPath}')` }}
                            />
                        )}
                    </div>
                )}
                <Box sx={{ maxWidth: 250, flexGrow: 1 }}>
                    {border && (
                        <Paper
                            square
                            elevation={0}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 30,
                                pl: 2,
                                bgcolor: 'background.default',
                            }}
                        />
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {showArrows && (
                            <div
                                onClick={activeStep !== 0 ? handleBack : null}
                                className={activeStep === 0 ? styles.disabled : styles.notDisabled}
                            >
                                <span className='material-icons'>arrow_back</span>
                            </div>
                        )}
                        <AutoPlaySwipeableViews
                            axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                            index={activeStep}
                            onChangeIndex={handleStepChange}
                            enableMouseEvents
                            interval={4000}
                        >
                            {data.map((step, index) => {
                                const showIconWarning =
                                    (step.name === 'VTPRO4' || step.name === 'VTPRO4-RIB') &&
                                    country.code === 'AR' &&
                                    !hideIconWarning;
                                return (
                                    <Stack key={step.label} alignItems='center' justifyContent='center'>
                                        {showIconWarning && (
                                            <div className={styles.iconWarning}>
                                                <i className={'material-icons'}>info</i>
                                            </div>
                                        )}
                                        {Math.abs(activeStep - index) <= 2 ? (
                                            <img
                                                style={{
                                                    height: imageHeight,
                                                    width: 'auto',
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={onClick}
                                                src={step.imgPath}
                                                alt={step.label}
                                            />
                                        ) : null}
                                    </Stack>
                                );
                            })}
                        </AutoPlaySwipeableViews>
                        {showArrows && (
                            <div
                                onClick={activeStep !== maxSteps - 1 ? handleNext : null}
                                className={activeStep === maxSteps - 1 ? styles.disabled : styles.notDisabled}
                            >
                                <span className='material-icons'>arrow_forward</span>
                            </div>
                        )}
                    </Box>
                    <Stack direction='row' spacing={0.5} justifyContent='center' alignItems='center'>
                        {data.map((step, index) => (
                            <Box
                                key={step.label}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: activeStep === index ? 'primary.main' : 'divider',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleStepChange(index)}
                            />
                        ))}
                    </Stack>
                </Box>
            </div>
        </ThemeProvider>
    );
}

export default TechnologySlider;
